// AI systems are first-class products but deliberately not in `products`:
// they have no price or Stripe checkout, and their cards CTA directly to
// Calendly / the live system instead of routing through ProductDetail.
export const aiSystems = [
  {
    id: 'scheduling-agent',
    slug: 'scheduling-agent',
    name: 'King Makers Scheduling Agent',
    status: 'Live in Production',
    category: 'AI Agent',
    tagline: 'Multi-tenant AI workforce scheduling with hard constraint enforcement and human approval.',
    description: 'Claude-powered shift assignment that enforces four hard scheduling constraints alongside tenant-configurable priorities. Every schedule carries a confidence score and passes a manager approval gate before anything publishes. Multi-tenant with row-level security, deployed in production.',
    features: [
      'Claude-powered shift assignment',
      'Four hard scheduling constraints, enforced',
      'Tenant-configurable priorities',
      'Confidence scoring on every schedule',
      'Manager approval gate before publishing',
      'Multi-tenant with row-level security',
    ],
    primaryCta: { label: 'Book a Discovery Call', href: 'https://calendly.com/hello-kingmakersinc/discovery', external: true },
    secondaryCta: { label: 'See the live system', href: 'https://scheduling.kingmakersinc.ca', external: true },
  },
  {
    id: 'consulting-autopilot',
    slug: 'consulting-autopilot',
    name: 'Consulting Autopilot',
    status: 'Agent Platform',
    category: 'AI Agent',
    tagline: 'Multi-agent business diagnostics from a single intake.',
    description: 'A coordinator drives sequential specialist agents (Researcher, Analyst, and Writer) to turn a single intake into a consulting-grade diagnostic brief. Built on the Claude Agent SDK.',
    features: [
      'Coordinator plus sequential specialist agents',
      'Researcher, Analyst, and Writer roles',
      'Consulting-grade diagnostic briefs',
      'Built on the Claude Agent SDK',
    ],
    primaryCta: { label: 'Book a Discovery Call', href: 'https://calendly.com/hello-kingmakersinc/discovery', external: true },
    secondaryCta: null,
  },
];

export const products = [
  {
    id: 1,
    slug: 'high-stakes-narrative-pack',
    name: 'High-Stakes Narrative Pack',
    subtitle: 'Notion Toolkit',
    category: 'moments',
    categoryLabel: 'Moments',
    price: 249,
    comingSoon: false,
    tagline: 'A decision-ready narrative system for moments where getting it wrong would be expensive.',
    description: 'A decision-ready narrative system for founders, operators, and professionals who need decisions from execs, boards, investors, or cross-functional leaders, and want alignment fast.',
    longDescription: 'Every high-stakes moment has a narrative structure hiding inside it. This toolkit gives you the complete system to find it, build it, de-risk it, and deliver it with precision. Whether you\'re presenting to your board, pitching investors, or navigating a difficult organizational decision, the narrative is what moves people from uncertainty to alignment.\n\nThis is not for early brainstorming, low-stakes updates, or coaching-seekers. It\'s for people who have a real moment coming and need to get it right.',
    includes: [
      '5-part system: Frame \u2192 Build \u2192 De-risk \u2192 Deploy \u2192 Land',
      'Decision Framing Intake + framing output',
      'Narrative spine template',
      'Objection map + embed patterns',
      'Deliverables: memo skeleton, slide flow, verbal outline',
      'In-room scripts (pushback, "more data" protocol, reset lines)',
      'Post-meeting follow-up template',
      'At least one full worked example',
    ],
    whoItsFor: 'Founders, operators, and professionals who need decisions from execs, boards, investors, or cross-functional leaders, and want alignment fast.',
    notFor: 'Early brainstorming, low-stakes updates, or coaching-seekers.',
    delivery: 'Instant access via Notion template duplication. Link revealed on thank-you page after payment.',
    checkoutUrl: 'https://buy.stripe.com/14AeVf64cgl3chogwUbjW00',
  },
  {
    id: 2,
    slug: 'decision-architecture-system',
    name: 'Decision Architecture System',
    category: 'decisions',
    categoryLabel: 'Decisions',
    price: null,
    comingSoon: true,
    tagline: 'Replace analysis paralysis with structured clarity.',
    description: 'A proven framework for breaking down complex, multi-stakeholder decisions into clear components. Map trade-offs, align stakeholders, and reach decisions with confidence.',
    longDescription: 'Complex decisions don\'t fail because people lack information. They fail because the decision itself is poorly structured. This system gives you a repeatable architecture for decomposing any decision (no matter how politically charged or technically complex) into components you can actually evaluate.',
    includes: [
      'Decision Decomposition Framework',
      'Trade-Off Mapping Tool',
      'Stakeholder Alignment Protocol',
      'Risk-Weighted Evaluation Matrix',
      'Implementation Decision Log',
    ],
    whoItsFor: 'Founders and leaders navigating complex decisions with multiple stakeholders and high consequences.',
    delivery: 'Digital toolkit. Details announced at launch.',
    checkoutUrl: null,
  },
  {
    id: 3,
    slug: 'org-clarity-system',
    name: 'Org Clarity System',
    category: 'execution',
    categoryLabel: 'Execution',
    price: null,
    comingSoon: true,
    tagline: 'Align your organization from strategy to execution.',
    description: 'When your org chart doesn\'t match reality. This system creates structural clarity: role boundaries, decision rights, communication flows, and execution cadence that actually work.',
    longDescription: 'Your org chart says one thing. Reality says another. Roles overlap, decisions get made twice (or not at all), and execution slows to a crawl. The Org Clarity System cuts through this fog with frameworks for defining who owns what, how decisions flow, and how work actually gets done.',
    includes: [
      'Organizational Clarity Audit',
      'Role & Responsibility Architecture',
      'Decision Rights Framework (RACI+)',
      'Communication Architecture Blueprint',
      'Execution Cadence Templates',
    ],
    whoItsFor: 'Growing teams (10\u2013100) where roles are blurring and execution is slowing.',
    delivery: 'Digital toolkit. Details announced at launch.',
    checkoutUrl: null,
  },
  {
    id: 4,
    slug: 'founder-time-leverage-system',
    name: 'Founder Time-Leverage System',
    category: 'energy',
    categoryLabel: 'Energy',
    price: null,
    comingSoon: true,
    tagline: 'Reclaim your time. Multiply your impact.',
    description: 'You\'re working 70-hour weeks but your business isn\'t growing proportionally. This system identifies where your time creates outsized value and builds leverage into every hour.',
    longDescription: 'There\'s a reason your business feels like it can\'t run without you: it\'s because you haven\'t built the systems to make it possible. This isn\'t about time management. It\'s about time architecture.',
    includes: [
      'Time Audit & Energy Mapping',
      'Delegation Architecture',
      'Leverage Multiplier Framework',
      'Calendar Architecture Blueprint',
      'Weekly Review Protocol',
    ],
    whoItsFor: 'Founders spending more than 50% of their time on work that doesn\'t move the needle.',
    delivery: 'Digital toolkit. Details announced at launch.',
    checkoutUrl: null,
  },
  {
    id: 5,
    slug: 'career-leverage-calculator',
    name: 'Career Leverage Calculator',
    category: 'focus',
    categoryLabel: 'Focus',
    price: null,
    comingSoon: true,
    tagline: 'Find your highest-leverage career move.',
    description: 'A strategic tool that maps your skills, market position, network strength, and opportunity cost to identify the single move that creates the most career leverage right now.',
    longDescription: 'At every career inflection point, there\'s one move that creates disproportionate leverage, and dozens that feel productive but aren\'t. This calculator helps you see the landscape clearly.',
    includes: [
      'Skills-to-Market Mapping',
      'Opportunity Cost Calculator',
      'Career Decision Matrix',
      'Network Leverage Assessment',
      '90-Day Execution Plan',
    ],
    whoItsFor: 'Professionals at an inflection point: considering a pivot, promotion, or new venture.',
    delivery: 'Digital toolkit. Details announced at launch.',
    checkoutUrl: null,
  },
];

export const services = [
  {
    id: 1,
    name: 'Narrative Advisory',
    tagline: 'Done-with-you narrative architecture for moments that matter.',
  },
  {
    id: 2,
    name: 'Decision Architecture Advisory',
    tagline: 'Facilitated decision-making for consequential choices.',
  },
  {
    id: 3,
    name: 'Time-Leverage Advisory',
    tagline: 'Redesign your operating system for time.',
  },
];

export const faqs = [
  {
    question: 'What exactly is King Makers?',
    answer: 'King Makers is an AI systems and consulting company based in Canada. We build production AI systems, including the Scheduling Agent (live in production) and the Consulting Autopilot (multi-agent business diagnostics), and we provide the consulting to deploy them inside your business. We also publish premium digital toolkits, starting with the High-Stakes Narrative Pack.',
  },
  {
    question: 'How do we start working together?',
    answer: 'Every engagement follows the same three-step motion: a 30-minute discovery call on your operations, constraints, and where decisions bottleneck; a diagnostic, in which our agent systems analyze your business and produce a consulting-grade assessment; then deployment, where we build or configure the AI system that removes the bottleneck and stay until it runs.',
    link: { label: 'Book a discovery call', href: 'https://calendly.com/hello-kingmakersinc/discovery' },
  },
  {
    question: 'Who is the High-Stakes Narrative Pack for?',
    answer: 'It\'s for founders, operators, and professionals who need decisions from execs, boards, investors, or cross-functional leaders, and want alignment fast. It\'s not for early brainstorming, low-stakes updates, or coaching-seekers.',
  },
  {
    question: 'What format does the product come in?',
    answer: 'The High-Stakes Narrative Pack is a Notion toolkit. After purchase, you get instant access via Notion template duplication: just click the link on the thank-you page and duplicate it to your workspace.',
  },
  {
    question: 'How quickly will I see results?',
    answer: 'The toolkit is designed for immediate application. The 5-part system (Frame \u2192 Build \u2192 De-risk \u2192 Deploy \u2192 Land) can be worked through in a single focused session. Most people start using it the same day they buy it.',
  },
  {
    question: 'What about the other products listed as "Coming Soon"?',
    answer: 'The High-Stakes Narrative Pack is the live toolkit today. The others listed as Coming Soon are still in development alongside our AI systems. Sign up on any product page to get notified when they launch.',
  },
  {
    question: 'Will there be advisory services?',
    answer: 'Consulting is live today and is core to how we work. Engagements start with a 30-minute discovery call booked via Calendly, followed by a diagnostic and, where it fits, deployment of an AI system inside your business.',
  },
  {
    question: 'Do you offer refunds?',
    answer: 'Because this is a digital product delivered instantly (including access to downloadable/copyable materials), all sales are final. If you experience an access issue (e.g., the link does not work or you cannot duplicate the template), contact us within 7 days of purchase and we will resolve it or provide access. If you accidentally purchased multiple times, contact us within 24 hours at hello@kingmakersinc.ca and we will help correct it.',
  },
  {
    question: 'Can I use this with my team?',
    answer: 'The toolkit is designed for individual use, but the frameworks work well in team settings. If you need a multi-seat license or team version, reach out and we\'ll work something out.',
  },
  {
    question: 'How do I access my purchase?',
    answer: 'After payment via Stripe, you\'ll be redirected to a thank-you page with a Notion template link. Click "Duplicate" to copy it to your Notion workspace. That\'s it: instant access.',
  },
  {
    question: 'What if my situation is unique?',
    answer: 'Most situations feel unique from the inside. The 5-part system is designed to handle the underlying patterns that create complexity in high-stakes moments. That said, if you want a thought partner on your specific situation, book a discovery call and we\'ll look at it together.',
  },
];
